import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FiArrowLeft, FiStar, FiUser, FiFileText, FiDownload } from 'react-icons/fi';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import IconWrapper from '../components/print-steps/IconWrapper';
import AntiPiracyBanner from '../components/print-bazaar/AntiPiracyBanner';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { fetchDocumentDetails } from '../lib/supabase/api/fetch-document-details';
import { PrintBazaarDoc } from '../types/documentTypes';
import PrintBazaarPaymentDialog from '../components/print-bazaar/PrintBazaarPaymentDialog';
import { useAuthStore } from '../store/authStore';
import { useWalletStore } from '../store/walletStore';
import { usePaymentTypeStore } from '../store/paymentTypeStore';
import { addPrintBazaarPurchase, checkPrintBazaarPurchase } from '../lib/supabase/api/print-bazaar-purchase';
import PrintBazaarPaymentService from '../services/PrintBazaarPaymentService';
import PrintSettingsDialog from '../modules/print-settings/PrintSettingsDialog';
import { usePrintNavigation } from '../hooks/usePrintNavigation';
import usePrintStore from '../store/printStore';
import InsufficientWalletBalanceService from '../services/InsufficientWalletBalanceService';
import { incrementPrintBazaarDownloads } from '../lib/supabase/api/fetch-print-bazaar-docs';
import { toast } from 'react-hot-toast';

const PrintBazaarDetailsPage: React.FC = () => {
  const { documentId } = useParams<{ documentId: string }>();
  const navigate = useNavigate();
  const [document, setDocument] = useState<PrintBazaarDoc | null>(null);
  const [loading, setLoading] = useState(true);
  const [showPaymentDialog, setShowPaymentDialog] = useState(false);
  const [isPurchased, setIsPurchased] = useState(false);
  const [showPrintSettings, setShowPrintSettings] = useState(false);
  const { user } = useAuthStore();
  const { balance } = useWalletStore();
  const { setPaymentType } = usePaymentTypeStore();
  const { navigateToPrintFlow } = usePrintNavigation();
  const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;

  useEffect(() => {
    const fetchDocument = async () => {
      if (!documentId) return;

      try {
        const doc = await fetchDocumentDetails(documentId);
        console.log('Fetched document:', doc);
        console.log('Document fields:', {
          title: doc?.title,
          description: doc?.description,
          cover_image_path: doc?.cover_image_path,
          preview_pages: doc?.preview_pages,
          cost: doc?.cost,
          tags: doc?.tags,
          page_count: doc?.page_count,
        });

        if (doc) {
          const docWithDefaultValues = {
            ...doc,
            preview_pages: Array.isArray(doc.preview_pages) ? doc.preview_pages : [],
            tags: Array.isArray(doc.tags) ? doc.tags : [],
            cost: doc.cost || 0,
            description: doc.description || 'No description available'
          };
          setDocument(docWithDefaultValues);
        } else {
          navigate('/print-bazaar');
        }
      } catch (error) {
        console.error('Error fetching document:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchDocument();
  }, [documentId, navigate]);

  useEffect(() => {
    const checkPurchaseStatus = async () => {
      if (!user || !documentId) return;
      try {
        const purchased = await checkPrintBazaarPurchase(user.id, documentId);
        setIsPurchased(purchased);
      } catch (error) {
        console.error('Error checking purchase status:', error);
      }
    };

    checkPurchaseStatus();
  }, [user, documentId]);

  const handlePurchaseClick = async () => {
    if (!user || !document) return;

    if (document.is_free) {
      try {
        await addPrintBazaarPurchase({
          user_id: user.id,
          doc_id: document.id,
          amount: 0,
          payment_type: 'FREE'
        });
        await incrementPrintBazaarDownloads(document.id);
        setIsPurchased(true);
        toast.success('Document added to your library');
      } catch (error) {
        console.error('Error adding free purchase:', error);
        toast.error('Failed to process free document');
      }
      return;
    }

    setShowPaymentDialog(true);
  };

  const handleWalletPayment = async () => {
    if (!user || !document) return;
    setPaymentType('wallet');
    
    try {
      if (balance === null) {
        toast.error('Unable to fetch wallet balance');
        return;
      }

      if (balance === 0) {
        // If wallet balance is 0, directly proceed with full Razorpay payment
        const paymentResult = await PrintBazaarPaymentService.handleRazorpayPayment(
          document.cost,
          user.id,
          document.id,
          user.user_metadata
        );

        if (paymentResult.success) {
          await addPrintBazaarPurchase({
            user_id: user.id,
            doc_id: document.id,
            amount: document.cost,
            payment_type: 'RAZORPAY',
            order_id: paymentResult.orderId
          });
          await incrementPrintBazaarDownloads(document.id);
          setIsPurchased(true);
        }
      } else if (balance >= document.cost) {
        // Handle normal wallet payment
        const paymentResult = await PrintBazaarPaymentService.handleWalletPayment(
          user.id, 
          document.id, 
          document.cost,
          user.user_metadata,
          'print_bazaar_purchase'
        );

        if (paymentResult.success) {
          await addPrintBazaarPurchase({
            user_id: user.id,
            doc_id: document.id,
            amount: document.cost,
            payment_type: 'WALLET',
            order_id: paymentResult.orderId
          });
          await incrementPrintBazaarDownloads(document.id);
          setIsPurchased(true);
        } else {
          toast.error('Payment failed');
        }
      } else {
        // Handle insufficient balance with split payment
        const splitPaymentResult = await InsufficientWalletBalanceService.handleSplitPayment(
          user.id,
          document.id,
          document.cost,
          balance,
          user.user_metadata,
          'PRINT_BAZAAR'  // Add payment type parameter
        );

        if (splitPaymentResult.success) {
          await addPrintBazaarPurchase({
            user_id: user.id,
            doc_id: document.id,
            amount: document.cost,
            payment_type: 'SPLIT',
            order_id: splitPaymentResult.orderId
          });
          await incrementPrintBazaarDownloads(document.id);
          setIsPurchased(true);
        } else {
          toast.error('Payment failed');
        }
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      toast.error('Payment failed');
    }
    
    setShowPaymentDialog(false);
  };

  const handleRazorpayPayment = async () => {
    if (!user || !document) return;
    setPaymentType('print_bazaar_purchase');
    
    try {
      const paymentResult = await PrintBazaarPaymentService.handleRazorpayPayment(
        document.cost,
        user.id,
        document.id,
        user.user_metadata
      );

      if (paymentResult.success) {
        await addPrintBazaarPurchase({
          user_id: user.id,
          doc_id: document.id,
          amount: document.cost,
          payment_type: 'RAZORPAY',
          order_id: paymentResult.orderId
        });
        await incrementPrintBazaarDownloads(document.id);
        setIsPurchased(true);
      }
    } catch (error) {
      console.error('Error processing Razorpay payment:', error);
    }
    
    setShowPaymentDialog(false);
  };

  const handlePrintNow = () => {
    if (!document) return;
    setShowPrintSettings(true);
  };

  const handlePrintSettingsClose = () => {
    setShowPrintSettings(false);
    // Navigate to print flow if dialog was closed via Continue button
    if (usePrintStore.getState().currentStep === 2) {
      navigateToPrintFlow(2);
    }
  };

  const renderPurchaseSection = () => (
    isPurchased ? (
      <div>
        <p className="text-green-600 font-medium">Purchased</p>
        <button 
          className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={handlePrintNow}
        >
          Print Now
        </button>
        {document && <PrintSettingsDialog
          isOpen={showPrintSettings}
          onClose={handlePrintSettingsClose}
          documentId={document.id}
          bazaarDoc={document}  // Pass the document data
        />}
      </div>
    ) : (
      <Button 
        className="w-full bg-blue-600 hover:bg-blue-700 mb-3 sm:mb-4"
        onClick={handlePurchaseClick}
      >
        {document?.is_free ? 'Get Free' : `Purchase for ₹${document?.cost}`}
      </Button>
    )
  );

  if (loading || !document) {
    return (
      <div className="min-h-screen flex flex-col bg-gray-50">
        <Header />
        <div className="flex-grow container mx-auto px-4 py-8">
          <div className="flex justify-center items-center min-h-[300px]">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-500"></div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <Header />

      <main className="flex-grow container mx-auto px-4 py-8">
        <button
          onClick={() => navigate('/print-bazaar')}
          className="inline-flex items-center px-4 py-2 text-gray-700 hover:text-blue-600 transition-colors duration-200 mb-6"
        >
          <IconWrapper icon={FiArrowLeft} className="mr-2" />
          <span className="font-medium">Back to PrintBazaar</span>
        </button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2 space-y-6">
            <Card className="overflow-hidden shadow-lg">
              <CardContent className="p-6">
                <Swiper
                  modules={[Navigation, Pagination]}
                  navigation
                  pagination={{ clickable: true }}
                  className="w-full aspect-[16/10] mb-4 sm:mb-6 rounded-lg overflow-hidden shadow-md"
                >
                  {document.cover_image_path && (
                    <SwiperSlide>
                      <div className="relative w-full h-full bg-gradient-to-r from-gray-50 to-white">
                        <img
                          src={`${supabaseUrl}/storage/v1/object/public/print-bazaar-docs/${document.cover_image_path}`}
                          alt="Cover"
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </SwiperSlide>
                  )}
                  {document.preview_pages?.map((url: string, index: number) => (
                    <SwiperSlide key={index}>
                      <div className="relative w-full h-full bg-gradient-to-r from-gray-50 to-white">
                        <img
                          src={`${supabaseUrl}/storage/v1/object/public/print-bazaar-docs/${url}`}
                          alt={`Preview ${index + 1}`}
                          className="w-full h-full object-contain"
                        />
                      </div>
                    </SwiperSlide>
                  ))}
                </Swiper>

                <div className="space-y-6">
                  <div>
                    <h1 className="text-3xl font-bold text-gray-900 mb-4">{document.title}</h1>
                    <div className="flex flex-wrap gap-4 items-center">
                      <div className="flex items-center px-3 py-1 bg-gray-100 rounded-full">
                        <IconWrapper icon={FiFileText} className="mr-2 text-gray-600" />
                        <span className="text-gray-700 font-medium">{document.page_count} pages</span>
                      </div>
                      {document.tags && document.tags.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {document.tags.map((tag: string, index: number) => (
                            <Badge key={index} variant="secondary" className="px-3 py-1 text-sm font-medium">
                              {tag}
                            </Badge>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="border-t border-gray-200 pt-6">
                    <h2 className="text-xl font-semibold text-gray-900 mb-4">About this document</h2>
                    <p className="text-gray-600 leading-relaxed mb-6">{document.description}</p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex items-center gap-2 hover:bg-gray-50"
                    >
                      <IconWrapper icon={FiStar} className="w-4 h-4" />
                      Share Document
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="overflow-hidden shadow-lg">
              <CardContent className="p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Document Stats</h2>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">{document.page_count}</div>
                    <div className="text-sm text-gray-600">Pages</div>
                  </div>
                  <div className="p-4 bg-gray-50 rounded-lg text-center">
                    <div className="text-2xl font-bold text-blue-600 mb-1">PDF</div>
                    <div className="text-sm text-gray-600">Format</div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-8 space-y-6">
              <Card className="overflow-hidden shadow-lg">
                <CardContent className="p-6">
                  <div className="text-center mb-6">
                    <div className="text-3xl font-bold text-blue-600 mb-2">
                      {document.is_free ? 'Free' : `₹${document.cost}`}
                    </div>
                    <p className="text-sm text-gray-600">Print-only access</p>
                  </div>

                  {renderPurchaseSection()}

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <div className="space-y-4">
                      <div className="flex items-center text-sm text-gray-600">
                        <IconWrapper icon={FiUser} className="mr-3 text-blue-600" />
                        <span>Professional Quality Print</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-600">
                        <IconWrapper icon={FiDownload} className="mr-3 text-blue-600" />
                        <span>Print-only access (no downloads)</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="overflow-hidden shadow-lg">
                <CardContent className="p-6">
                  <h3 className="font-semibold text-gray-900 mb-4">Support the Creator</h3>
                  <p className="text-sm text-gray-600">
                    70% of your purchase goes directly to supporting the document creator.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>

        <PrintBazaarPaymentDialog
          isOpen={showPaymentDialog}
          onClose={() => setShowPaymentDialog(false)}
          onWalletPayment={handleWalletPayment}
          onRazorpayPayment={handleRazorpayPayment}
          amount={document?.cost || 0}
          walletBalance={balance}
          showSplitAmount={balance !== null && balance < (document?.cost || 0)}
          walletDeductAmount={balance || 0}
          razorpayAmount={(document?.cost || 0) - (balance || 0)}
        />
      </main>

      <Footer />
    </div>
  );
};

export default PrintBazaarDetailsPage;
